import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Group,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconDownload, IconSearch } from '@tabler/icons-react';
import { QueryState, fmtMoney, fmtPct, useChartColors } from './KpiPage';
import { toCsv, downloadCsv, stampedFilename } from './csvExport';
import useNexusQuery, { field, num } from '../../hooks/useNexusQuery';

// The exportable "Managed Parts Sold" table. Sibling of ManagedPartsTable, but a different
// question: that one lists the managed CATALOG as it stands today, this one lists what actually
// SOLD in the reporting window — so it takes the window as a prop and re-runs when it changes.
//
// The margin columns are Pentagon's own booked figures, not a re-derivation. CostSource is the
// column that earns this table its keep: it says WHICH cost regime produced the margin, which is
// the one thing you cannot read off the margin itself. See docs/pentagon_warehouse_cost.md.

const PAGE_SIZE = 25;

// The query caps at 20,000 rows. Three years of managed sales is ~12,000, so this should never
// bind — but if it does, say so rather than quietly showing a truncated set as if it were whole.
const ROW_CAP = 20000;

const COLUMNS = [
  { key: 'invoiceDate', label: 'Invoice Date' },
  { key: 'docNo', label: 'Invoice' },
  { key: 'line', label: 'Line' },
  { key: 'partnumber', label: 'Part Number' },
  { key: 'description', label: 'Description' },
  { key: 'cls', label: 'Class' },
  { key: 'condition', label: 'Condition' },
  { key: 'serialNumber', label: 'Serial Number' },
  { key: 'customer', label: 'Customer' },
  { key: 'keyCustomer', label: 'Key Customer' },
  { key: 'qty', label: 'Qty Shipped' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'unitPrice', label: 'Unit Price' },
  { key: 'ssp', label: 'SSP at Sale' },
  { key: 'sspExtended', label: 'SSP Extended' },
  { key: 'devPct', label: 'Deviation %' },
  { key: 'dos', label: 'Days on Shelf' },
  { key: 'dosBucket', label: 'DOS Bucket' },
  { key: 'bookedCost', label: 'Booked Cost' },
  { key: 'estGm', label: 'Est. Gross Margin' },
  { key: 'estGmPct', label: 'Est. Gross Margin %' },
  { key: 'costBasis', label: 'Cost Basis' },
  { key: 'costSource', label: 'Cost Source' },
  { key: 'virtualWh', label: 'Virtual WH' },
  { key: 'virtualWhName', label: 'Virtual WH Name' },
  { key: 'virtualWhGroup', label: 'Virtual WH Group' },
];

// Shown on screen. The CSV carries all 26 columns above — the table would be unreadable with them
// all, and the point of the export is that it gets bucketed in Excel.
const VISIBLE = [
  'invoiceDate',
  'partnumber',
  'description',
  'cls',
  'condition',
  'customer',
  'qty',
  'revenue',
  'ssp',
  'dos',
  'estGmPct',
  'virtualWh',
  'virtualWhGroup',
  'costSource',
];

const DOS_ORDER = ['0-60', '61-120', '121-180', '181-270', '271+', '(unknown)'];

/**
 * Distinct non-empty values of `key` across `rows`, for a filter dropdown. Alphabetical unless an
 * explicit `order` is given — DOS buckets have to read 0-60 → 271+, not "0-60, 121-180, 181-270".
 *
 * Module scope rather than inside the component so the useMemos that call it can depend on `rows`
 * alone, which is the honest dependency.
 */
function distinctValues(rows, key, order) {
  const vals = new Set(rows.map((r) => r[key]).filter(Boolean));
  return order ? order.filter((v) => vals.has(v)) : [...vals].sort();
}

export default function ManagedSoldTable({ window: reportingWindow }) {
  const c = useChartColors();
  const detail = useNexusQuery('initiative-scorecard-managed-sold-detail', reportingWindow);

  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState(null);
  const [conditionFilter, setConditionFilter] = useState(null);
  const [basisFilter, setBasisFilter] = useState(null);
  const [dosFilter, setDosFilter] = useState(null);
  const [whGroupFilter, setWhGroupFilter] = useState(null);
  const [page, setPage] = useState(1);

  const rows = useMemo(
    () =>
      detail.rows.map((r) => ({
        invoiceDate: field(r, 'InvoiceDate') ?? '',
        docNo: field(r, 'DocNo') ?? '',
        line: field(r, 'Line') ?? '',
        partnumber: field(r, 'Partnumber') ?? '',
        description: field(r, 'Description') ?? '',
        cls: field(r, 'Class') ?? '',
        condition: field(r, 'Condition') ?? '',
        serialNumber: field(r, 'SerialNumber') ?? '',
        customer: field(r, 'Customer') ?? '',
        keyCustomer: field(r, 'KeyCustomer') ?? '',
        qty: num(r, 'QtyShipped'),
        revenue: num(r, 'Revenue'),
        unitPrice: num(r, 'UnitPrice'),
        // SSP and deviation are genuinely absent on unpriced parts. Keep them null rather than
        // coercing to 0, or an unpriced line reads as "priced at zero, sold 100% over".
        ssp: field(r, 'AtSaleSSP') == null ? null : num(r, 'AtSaleSSP'),
        sspExtended: field(r, 'SspExtended') == null ? null : num(r, 'SspExtended'),
        devPct: field(r, 'DevPct') == null ? null : num(r, 'DevPct'),
        dos: field(r, 'DaysOnShelf') == null ? null : num(r, 'DaysOnShelf'),
        dosBucket: field(r, 'DosBucket') ?? '',
        bookedCost: num(r, 'BookedCost'),
        estGm: num(r, 'EstGrossMargin'),
        estGmPct: field(r, 'EstGrossMarginPct') == null ? null : num(r, 'EstGrossMarginPct'),
        costBasis: field(r, 'CostBasis') ?? '',
        costSource: field(r, 'CostSource') ?? '',
        virtualWh: field(r, 'VirtualWarehouseCode') ?? '',
        virtualWhName: field(r, 'VirtualWarehouseName') ?? '',
        virtualWhGroup: field(r, 'VirtualWhgroup') ?? '',
      })),
    [detail.rows]
  );

  const classOptions = useMemo(() => distinctValues(rows, 'cls'), [rows]);
  const conditionOptions = useMemo(() => distinctValues(rows, 'condition'), [rows]);
  const basisOptions = useMemo(() => distinctValues(rows, 'costBasis'), [rows]);
  const dosOptions = useMemo(() => distinctValues(rows, 'dosBucket', DOS_ORDER), [rows]);
  const whGroupOptions = useMemo(() => distinctValues(rows, 'virtualWhGroup'), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!classFilter || r.cls === classFilter) &&
        (!conditionFilter || r.condition === conditionFilter) &&
        (!basisFilter || r.costBasis === basisFilter) &&
        (!whGroupFilter || r.virtualWhGroup === whGroupFilter) &&
        (!dosFilter || r.dosBucket === dosFilter) &&
        (!q ||
          r.partnumber.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q) ||
          r.customer.toLowerCase().includes(q))
    );
  }, [rows, search, classFilter, conditionFilter, basisFilter, dosFilter, whGroupFilter]);

  // Totals follow the filters, so "AR parts out of BCR" gives you that slice's margin directly
  // rather than requiring a trip through Excel for the obvious question.
  const totals = useMemo(() => {
    const revenue = filtered.reduce((s, r) => s + r.revenue, 0);
    const gm = filtered.reduce((s, r) => s + r.estGm, 0);
    return {
      units: filtered.reduce((s, r) => s + r.qty, 0),
      revenue,
      gm,
      gmPct: revenue ? (gm / revenue) * 100 : 0,
    };
  }, [filtered]);

  // Any filter change can leave you past the end of the new result set.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetPage = (fn) => (v) => {
    fn(v);
    setPage(1);
  };

  const visibleCols = COLUMNS.filter((col) => VISIBLE.includes(col.key));

  const cell = (r, key) => {
    switch (key) {
      case 'qty':
        return r.qty.toLocaleString();
      case 'revenue':
        return fmtMoney(r.revenue);
      case 'ssp':
        return r.ssp == null ? <Text span size="xs" c="dimmed">no SSP</Text> : fmtMoney(r.ssp);
      case 'dos':
        return r.dos == null ? '—' : r.dos.toLocaleString();
      case 'estGmPct':
        return r.estGmPct == null ? '—' : (
          <Text span c={r.estGmPct < 0 ? c.negative : undefined}>{fmtPct(r.estGmPct)}</Text>
        );
      case 'costSource':
        return (
          <Badge size="sm" variant="light" color={r.costSource.startsWith('Lot payback') ? 'orange' : 'blue'}>
            {r.costSource}
          </Badge>
        );
      case 'partnumber':
        return <Text span ff="monospace" size="sm">{r.partnumber}</Text>;
      case 'description':
        // Descriptions run long and would otherwise set the table width on their own.
        return (
          <Text span size="sm" lineClamp={1} title={r.description}>
            {r.description}
          </Text>
        );
      case 'virtualWh':
        return (
          <Text span ff="monospace" size="xs" title={r.virtualWhName}>
            {r.virtualWh}
          </Text>
        );
      case 'virtualWhGroup':
        return (
          <Badge size="sm" variant="light" color={r.virtualWhGroup === '(blank)' ? 'gray' : 'indigo'}>
            {r.virtualWhGroup}
          </Badge>
        );
      case 'customer':
        return (
          <Group gap={4} wrap="nowrap">
            <Text span size="sm" lineClamp={1}>{r.customer}</Text>
            {r.keyCustomer === 'Y' && (
              <Badge size="xs" variant="light" color="teal">key</Badge>
            )}
          </Group>
        );
      default:
        return r[key];
    }
  };

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2}>
            <Text fw={600}>Managed parts sold</Text>
            <Text size="xs" c="dimmed">
              Every managed line invoiced in the selected period, with the SSP that was live at the
              time of sale, days on shelf at sale, and estimated gross margin. Eleven of the 24
              columns are shown; the CSV carries all of them.
            </Text>
          </Stack>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconDownload size={14} />}
            disabled={!filtered.length}
            onClick={() =>
              downloadCsv(stampedFilename('managed-parts-sold'), toCsv(filtered, COLUMNS))
            }
          >
            CSV ({filtered.length.toLocaleString()})
          </Button>
        </Group>

        {/* Cost Source is the answer to "how do we tell repair-only lots from ones still repaying
            an airframe", so it gets an explanation rather than being left as a bare label. */}
        <Text size="xs" c="dimmed">
          <b>Cost Source</b> says which regime produced the margin.{' '}
          <b>Unit cost (repair only)</b> — the lot has costed out, so the cost of the sale is the
          unit&rsquo;s own booked cost, which is the repair we accrued into it and nothing else.{' '}
          <b>Lot payback N%</b> — the lot still carries base cost, so N% of the sale is clawed back
          to repay the airframe. Margin is Pentagon&rsquo;s booked figure, not a re-derivation, but
          the underlying model reproduces Pentagon to ~93%, so treat it as an estimate.
        </Text>

        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder="Search part number, description or customer"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => resetPage(setSearch)(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 240 }}
            size="xs"
          />
          <Select placeholder="All classes" data={classOptions} value={classFilter}
                  onChange={resetPage(setClassFilter)} clearable size="xs" w={120} />
          <Select placeholder="All conditions" data={conditionOptions} value={conditionFilter}
                  onChange={resetPage(setConditionFilter)} clearable size="xs" w={140} />
          {/* Wider than its siblings: the cost-basis values carry qualifiers
              ("Purchasing (supply chain stock)") that are the whole point of the label. */}
          <Select placeholder="All cost bases" data={basisOptions} value={basisFilter}
                  onChange={resetPage(setBasisFilter)} clearable size="xs" w={230} />
          <Select placeholder="All DOS buckets" data={dosOptions} value={dosFilter}
                  onChange={resetPage(setDosFilter)} clearable size="xs" w={150} />
          <Select placeholder="All WH groups" data={whGroupOptions} value={whGroupFilter}
                  onChange={resetPage(setWhGroupFilter)} clearable size="xs" w={150} />
        </Group>

        <QueryState isLoading={detail.isLoading} error={detail.error} isEmpty={!rows.length}>
          <>
            <Group gap="lg" wrap="wrap">
              <Text size="xs" c="dimmed">
                {filtered.length.toLocaleString()} of {rows.length.toLocaleString()} lines
              </Text>
              <Text size="xs" c="dimmed">
                {totals.units.toLocaleString()} units · {fmtMoney(totals.revenue)} revenue ·{' '}
                {fmtMoney(totals.gm)} est. margin ({fmtPct(totals.gmPct)})
              </Text>
              {rows.length >= ROW_CAP && (
                <Tooltip label="The stored query returns at most 20,000 rows. Narrow the date range to see the rest.">
                  <Badge size="sm" color="yellow" variant="light">
                    Truncated at {ROW_CAP.toLocaleString()}
                  </Badge>
                </Tooltip>
              )}
            </Group>

            <div style={{ overflowX: 'auto' }}>
              <Table striped highlightOnHover withTableBorder verticalSpacing="xs" fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    {visibleCols.map((col) => (
                      <Table.Th key={col.key}>{col.label}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pageRows.map((r) => (
                    <Table.Tr key={`${r.docNo}-${r.line}`}>
                      {visibleCols.map((col) => (
                        <Table.Td key={col.key}>{cell(r, col.key)}</Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                  {!pageRows.length && (
                    <Table.Tr>
                      <Table.Td colSpan={visibleCols.length}>
                        <Text size="sm" c="dimmed" ta="center" py="md">
                          No lines match those filters.
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </div>

            {pageCount > 1 && (
              <Group justify="center">
                <Pagination value={safePage} onChange={setPage} total={pageCount} size="sm" withEdges />
              </Group>
            )}
          </>
        </QueryState>
      </Stack>
    </Card>
  );
}
